/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import { getBotVersion } from '../../api/botsApi';
import BacktestConfigForm from '../../components/bots/BacktestConfigForm'; // Import the form

const CreateEditBacktestConfigPage = () => {
  const { botId, versionId, configId } = useParams(); // configId will be undefined for create
  const [version, setVersion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const isEditMode = Boolean(configId);

  useEffect(() => {
    const fetchVersion = async () => {
      setIsLoading(true);
      try {
        const versionData = await getBotVersion(versionId);
        setVersion(versionData);
      } catch (err) {
        console.error("Failed to fetch bot version:", err);
        setPageError(err.message || "Failed to load bot version details.");
      } finally {
        setIsLoading(false);
      }
    };
    if (versionId) {
      fetchVersion();
    }
  }, [versionId]);

  const pageTitle = () => {
    if (isLoading) return isEditMode ? "Loading Backtest Configuration..." : "Create New Backtest Configuration...";
    let title = isEditMode ? "Edit Backtest Configuration" : "Create New Backtest Configuration";
    if (version) {
      title += ` for ${version.version_name || 'Unnamed Version'}`;
    }
    return title;
  };
  
  if (pageError) {
    return (
      <PageLayout
        title="Error"
        backButton={{ label: 'Back to Version', to: `/bots/${botId}/versions/${versionId}`, preferHistory: false, hideLabelOnMobile: true }}
      >
        <p className="text-destructive">{pageError}</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={pageTitle()}
      backButton={{ label: 'Back to Version', to: `/bots/${botId}/versions/${versionId}`, preferHistory: false, hideLabelOnMobile: true }}
    >
      {isLoading ? (
        <p>Loading version details...</p>
      ) : version ? ( // Ensure version is loaded before rendering form
        <BacktestConfigForm /> // botId, versionId, configId are picked up by useParams in the form
      ) : (
        !pageError && <p>Bot version details could not be loaded.</p> // Should be covered by pageError check already
      )}
    </PageLayout>
  );
};

export default CreateEditBacktestConfigPage;
